import { makeAutoObservable } from 'mobx'
import { type IResponseInternationalRegulatoryBundleItem } from 'entities/Compliance'

export class RegulatoryBundleItem {
  private _id: IResponseInternationalRegulatoryBundleItem['id'] | null = null
  private _organizationId: IResponseInternationalRegulatoryBundleItem['organizationId'] | null =
    null
  private _vendorId: IResponseInternationalRegulatoryBundleItem['vendorId'] | null = null
  private _status: IResponseInternationalRegulatoryBundleItem['status'] | null = null
  private _step: IResponseInternationalRegulatoryBundleItem['step'] | null = null
  private _businessData: IResponseInternationalRegulatoryBundleItem['businessData'] | null = null
  private _addressData: IResponseInternationalRegulatoryBundleItem['addressData'] | null = null
  private _authorizedRepresentativeData:
    | IResponseInternationalRegulatoryBundleItem['authorizedRepresentativeData']
    | null = null
  private _createdAt: IResponseInternationalRegulatoryBundleItem['createdAt'] | null = null
  private _updatedAt: IResponseInternationalRegulatoryBundleItem['updatedAt'] | null = null
  private _submittedAt: IResponseInternationalRegulatoryBundleItem['submittedAt'] | null = null
  private _regulation: IResponseInternationalRegulatoryBundleItem['regulation'] | null = null
  private _registrationErrors:
    | IResponseInternationalRegulatoryBundleItem['registrationErrors']
    | null = null

  constructor(data: IResponseInternationalRegulatoryBundleItem) {
    makeAutoObservable(this)

    this.syncData(data)
  }

  syncData = ({
    addressData,
    authorizedRepresentativeData,
    businessData,
    createdAt,
    id,
    organizationId,
    regulation,
    status,
    step,
    submittedAt,
    updatedAt,
    vendorId,
    registrationErrors,
  }: IResponseInternationalRegulatoryBundleItem) => {
    this._id = id
    this._organizationId = organizationId
    this._vendorId = vendorId
    this._status = status
    this._step = step
    this._businessData = businessData
    this._addressData = addressData
    this._authorizedRepresentativeData = authorizedRepresentativeData
    this._createdAt = createdAt
    this._updatedAt = updatedAt
    this._submittedAt = submittedAt
    this._regulation = regulation
    this._registrationErrors = registrationErrors
  }

  get id() {
    return this._id
  }

  get organizationId() {
    return this._organizationId
  }

  get vendorId() {
    return this._vendorId
  }

  get step() {
    return this._step
  }

  get status() {
    return this._status
  }

  get isSubmitted() {
    if (!this._status) return false

    return this._status !== 'in_progress'
  }

  get isStatusInProgress() {
    return this._status === 'in_progress'
  }

  get isStatusPending() {
    return this._status === 'pending'
  }

  get isStatusInCarrierReview() {
    return this._status === 'in_carrier_review'
  }

  get isStatusApproved() {
    return this._status === 'approved'
  }

  get isStatusCreated() {
    return this._status === 'created'
  }

  get isStatusFailed() {
    return this._status === 'failed'
  }

  get isStatusUnderReview() {
    return this._status === 'under_review'
  }

  get businessData() {
    return this._businessData
  }

  get addressData() {
    return this._addressData
  }

  get authorizedRepresentativeData() {
    return this._authorizedRepresentativeData
  }

  get createdAt() {
    return this._createdAt
  }

  get updatedAt() {
    return this._updatedAt
  }

  get submittedAt() {
    return this._submittedAt
  }

  get regulation() {
    return this._regulation
  }

  get secondLine() {
    return `${this.addressData?.city}, ${this.addressData?.state} ${this.addressData?.zipCode}`
  }

  get registrationErrors() {
    return this._registrationErrors?.map((error) => error.message).join('\n')
  }
}
